<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from '~/composables/useI18n'
import {
    formatPath,
    getInspirationOutputType,
    getInspirationType,
} from '~/utils/inspiration'
import { useUserStore } from '@/stores/user'
import { emitOpenGoodsModal } from '~/utils/emitter'
import { noticeMessagePopup } from '~/utils/common'
import { prepareAllowedInspirationNavigationItem } from '~/utils/compliance-models'

const { t, locale } = useI18n()
const userStore = useUserStore()

const props = defineProps({
    showDataItemInfo: {
        type: Object,
        default: () => ({}),
    },
})

const emit = defineEmits(['closeMask', 'changeLikeStateForItem'])
const getInspirationId = (item: any) => item?.ins_id || item?.id

// 语言代码到浏览器 locale 的映射（用于时间格式化）
const localeMap: Record<string, string> = {
    zh: 'zh-CN',
    en: 'en-US',
    ja: 'ja-JP',
    ko: 'ko-KR',
    es: 'es-ES',
    fr: 'fr-FR',
    de: 'de-DE',
    ru: 'ru-RU',
    pt: 'pt-PT',
    it: 'it-IT',
    ar: 'ar-SA',
}

const isVipUser = computed(() => {
    return !!userStore.accountSummary?.sub_info?.subscription_type
})

const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const browserLocale = localeMap[locale.value] || 'en-US'
    return date.toLocaleDateString(browserLocale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    })
}

/** ========= 媒体加载态（避免缓存/水合导致事件丢失卡 loading） ========= */
const mediaLoading = ref(true)
const mainMediaRef = ref<HTMLImageElement | HTMLVideoElement | null>(null)
let ensureLoadedTimer: ReturnType<typeof setTimeout> | null = null

const clearEnsureLoadedTimer = () => {
    if (ensureLoadedTimer) {
        clearTimeout(ensureLoadedTimer)
        ensureLoadedTimer = null
    }
}

const ensureMediaLoadedState = () => {
    const el = mainMediaRef.value
    if (!el) return

    if (el instanceof HTMLImageElement) {
        if (el.complete && el.naturalWidth > 0) mediaLoading.value = false
        return
    }

    const video = el as HTMLVideoElement
    if (video.readyState >= 2) mediaLoading.value = false
}

const handleMediaLoaded = () => {
    clearEnsureLoadedTimer()
    mediaLoading.value = false
}

const handleMediaError = (e?: Event) => {
    // 避免永远卡 loading
    console.error('媒体加载失败，关闭 loading', e)
    clearEnsureLoadedTimer()
    mediaLoading.value = false
}

watch(
    () => props.showDataItemInfo?.output,
    async val => {
        clearEnsureLoadedTimer()
        mediaLoading.value = !!val
        await nextTick()
        ensureMediaLoadedState()
        ensureLoadedTimer = setTimeout(() => ensureMediaLoadedState(), 200)
    },
    { immediate: true }
)

onMounted(() => {
    nextTick(() => ensureMediaLoadedState())
})

onBeforeUnmount(() => {
    clearEnsureLoadedTimer()
})

/** ========= 输出尺寸（resolution 优先，其次实际加载） ========= */
const actualSize = ref<{ width: number; height: number } | null>(null)
const loadActualSize = () => {
    const url = props.showDataItemInfo?.output_with_watermark
    if (!url) return
    actualSize.value = null

    const isVideo =
        getInspirationType(props.showDataItemInfo.type) === 'VIDEO' ||
        getInspirationOutputType(url) === 'VIDEO'

    if (isVideo) {
        const video = document.createElement('video')
        video.preload = 'metadata'
        video.onloadedmetadata = () => {
            actualSize.value = { width: video.videoWidth, height: video.videoHeight }
        }
        video.onerror = () => {
            // 静默失败即可
        }
        video.src = url
    } else {
        const img = new Image()
        img.onload = () => {
            actualSize.value = { width: img.naturalWidth, height: img.naturalHeight }
        }
        img.onerror = () => {
            // 静默失败即可
        }
        img.src = url
    }
}

watch(
    () => props.showDataItemInfo?.output_with_watermark,
    () => loadActualSize(),
    { immediate: true }
)

const outputSize = computed(() => {
    const item: any = props.showDataItemInfo

    const resolution = item?.param?.resolution
    if (resolution) {
        const key = String(resolution).toLowerCase()
        const resolutionMap: Record<string, string> = {
            '720p': '720 × 1280',
            '1080p': '1080 × 1920',
            '1k': '1080 × 1920',
            '2k': '1440 × 2560',
            '4k': '2160 × 3840',
        }
        return resolutionMap[key] || resolution
    }

    if (actualSize.value) {
        return `${actualSize.value.width} × ${actualSize.value.height}`
    }

    return ''
})

/** ========= 文案/显示字段 ========= */
const displayTitle = computed(() => {
    // 截图里展示的是类似 “CyberPunk”，这里优先用 type_name
    return (
        (props.showDataItemInfo as any)?.type_name ||
        (props.showDataItemInfo as any)?.param?.model ||
        t('inspiration.title')
    )
})

// const titleInitials = computed(() => {
//     const s = String(displayTitle.value || '').trim()
//     if (!s) return 'AI'
//     const parts = s.split(/\s+/).filter(Boolean)
//     const first = parts[0]?.[0] || ''
//     const second = parts.length > 1 ? parts[1]?.[0] : s[1] || ''
//     const res = (first + (second || '')).toUpperCase()
//     return res || 'AI'
// })

const userAvatarAlt = computed(() => {
    const userName = props.showDataItemInfo?.user?.user_name
    return userName
        ? t('inspiration.userAvatar', { name: userName })
        : t('inspiration.defaultAvatar')
})

const promptText = computed(() => (props.showDataItemInfo as any)?.param?.prompt || '')

const isVideo = computed(() => {
    const item: any = props.showDataItemInfo
    return (
        getInspirationType(item?.type) === 'VIDEO' ||
        getInspirationOutputType(item?.output) === 'VIDEO'
    )
})

const generateAltText = computed(() => {
    const item: any = props.showDataItemInfo
    const contentType = isVideo.value ? t('inspiration.video') : t('inspiration.image')
    const model = item?.param?.model || 'AI'
    const prompt = item?.param?.prompt || ''
    const promptSnippet =
        prompt.length > 80 ? prompt.slice(0, 80) + '...' : prompt
    return t('inspiration.aiGenerated', {
        type: contentType,
        model: model,
        prompt: promptSnippet || '',
    })
})

/** ========= 交互：展开详情 / 复制 / 下载 / 做同款 ========= */
const isExpanded = ref(false)

const copyText = async (text: string) => {
    try {
        await navigator.clipboard.writeText(text)
        noticeMessagePopup(t('inspiration.copySuccess'), '', 'success', 3000)
    } catch (err) {
        console.error('复制失败:', err)
        noticeMessagePopup(t('inspiration.copyFailed'), '', 'error', 3000)
    }
}

const copyPrompt = async () => {
    if (!promptText.value) return
    await copyText(promptText.value)
}

const isClient = import.meta.client
const copyLink = () => {
    if (!isClient) return
    const origin = window.location.origin
    const inspirationId = (props.showDataItemInfo as any)?.ins_id
    const path = `${origin}/creation/${inspirationId}`
    copyText(path)
}

/**
 * 生成参考图片的 alt 文本
 */
const generateRefImageAlt = (index: number | string) => {
    const item = props.showDataItemInfo
    const model = item?.param?.model || 'AI'
    const idx = typeof index === 'string' ? Number(index) : index
    const safeIndex = Number.isFinite(idx) ? idx : 0
    return `Reference image ${safeIndex + 1} for ${model} generation`
}

const isDownloadMenuOpen = ref(false)
const toggleDownloadMenu = () => {
    isDownloadMenuOpen.value = !isDownloadMenuOpen.value
}
const closeDownloadMenu = () => {
    isDownloadMenuOpen.value = false
}

const downloadMedia = async (url: string, filename: string) => {
    noticeMessagePopup(t('inspiration.preparingDownload'), '', 'success', 3000)
    try {
        const response = await fetch(url, { mode: 'cors', cache: 'no-cache' })
        if (!response.ok) throw new Error(`下载失败: ${response.status}`)

        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.style.display = 'none'
        link.href = blobUrl
        link.download = filename

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
        console.error('下载失败:', error)
        noticeMessagePopup(t('inspiration.downloadFailed'), '', 'error', 3000)
    }
}

const downloadWithWatermark = () => {
    const url = (props.showDataItemInfo as any)?.output_with_watermark
    if (!url) return
    const inspirationId = getInspirationId(props.showDataItemInfo)
    const filename = `weryai_inspiration_${inspirationId}_watermark${getInspirationType(
        (props.showDataItemInfo as any)?.type
    )}`
    closeDownloadMenu()
    downloadMedia(url, filename)
}

const downloadWithoutWatermark = () => {
    // VIP 校验
    if (!isVipUser.value) {
        noticeMessagePopup(t('inspiration.vipRequired'), '', 'warning', 3000)
        setTimeout(() => emitOpenGoodsModal(), 1000)
        return
    }

    const url = (props.showDataItemInfo as any)?.output
    if (!url) {
        noticeMessagePopup(
            t('inspiration.noWatermarkUrlNotAvailable'),
            '',
            'warning',
            3000
        )
        return
    }

    const inspirationId = getInspirationId(props.showDataItemInfo)
    const filename = `weryai_inspiration_${inspirationId}_no_watermark${getInspirationType(
        (props.showDataItemInfo as any)?.type
    )}`
    closeDownloadMenu()
    downloadMedia(url, filename)
}

const navigationChange = (item: any) => {
    if (!isClient) return
    const targetPath = formatPath(item.type, item.param?.template_id, item.param?.template_path)
    const origin = window.location.origin
    const timestamp = Date.now()
    const { item: navigationItem, defaultModel } =
        prepareAllowedInspirationNavigationItem(item)
    const defaultModelQuery = defaultModel
        ? `&defaultModel=${encodeURIComponent(defaultModel)}`
        : ''
    const path = `${origin}/${locale.value}/${targetPath}?customType=inspiration&t=${timestamp}${defaultModelQuery}`

    const dataWriteBack = JSON.stringify(navigationItem)
    localStorage.setItem('inspirationData', dataWriteBack)

    emit('closeMask')
    location.href = path
}

watch(
    () => props.showDataItemInfo,
    () => {
        isDownloadMenuOpen.value = false
        isExpanded.value = false
    }
)
</script>

<template>
    <div class="mobile-ugc-mask" @click.self="emit('closeMask')">
        <div class="mobile-ugc" @click="closeDownloadMenu">
            <button class="close-btn" :aria-label="t('common.close')" @click="emit('closeMask')">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                        d="M8.00001 7.05334L11.2933 3.76L12.24 4.70667L8.94668 8.00001L12.24 11.2933L11.2933 12.24L8.00001 8.94667L4.70668 12.24L3.76001 11.2933L7.05334 8.00001L3.76001 4.70667L4.70668 3.76L8.00001 7.05334Z"
                        fill="white" />
                </svg>
            </button>

            <div class="media-area">
                <div class="media-loading" v-if="mediaLoading">
                    <l-dot-spinner size="46" speed="0.9" color="var(--el-menu-active-color)"></l-dot-spinner>
                </div>

                <img v-if="!isVideo" ref="mainMediaRef" class="media" :class="{ 'is-loading': mediaLoading }"
                    :src="(showDataItemInfo as any).output" :alt="generateAltText" @load="handleMediaLoaded"
                    @error="handleMediaError" />

                <video v-else ref="mainMediaRef" class="media" :class="{ 'is-loading': mediaLoading }"
                    :src="(showDataItemInfo as any).output" muted autoplay playsinline :controls="!mediaLoading"
                    @loadeddata="handleMediaLoaded" @error="handleMediaError"></video>
            </div>

            <div class="sheet">
                <div class="sheet-header">
                    <div class="show-prompt-actions">
                        <section class="userinfo-left">
                            <div class="title-block">
                                <div class="title-avatar">
                                    <img v-if="showDataItemInfo.user?.avatar" :src="showDataItemInfo.user?.avatar"
                                        :alt="userAvatarAlt" class="avatar" />
                                    <img v-else
                                        src="https://static.weryai.com/growth/image/9ead8a3f9b5dd9d44367d3aba6ac8a93.webp"
                                        :alt="t('inspiration.defaultAvatar')" class="avatar" />
                                </div>
                                <div class="title-text">
                                    {{ showDataItemInfo.user?.user_name || '' }}
                                </div>
                            </div>
                            <div class="prompt-card" >
                                <div class="prompt-text" :class="{'multilineText': !isExpanded}">{{ promptText }}</div>
                                <!-- <button
                        class="prompt-copy"
                        :aria-label="t('inspiration.copyPrompt') || t('common.copy')"
                        @click.stop="copyPrompt"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            aria-hidden="true"
                        >
                            <rect
                                x="9"
                                y="9"
                                width="13"
                                height="13"
                                rx="2"
                                ry="2"
                            ></rect>
                            <path
                                d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
                            ></path>
                        </svg>
                    </button> -->
                            </div>
                        </section>
                        <section class=actions>
                            <button class="icon-btn like" :aria-label="(showDataItemInfo as any).has_like
                                    ? t('common.unlike')
                                    : t('common.like')
                                " @click="
                                ($event: MouseEvent) =>
                                    emit(
                                        'changeLikeStateForItem',
                                        $event,
                                        showDataItemInfo
                                    )
                            ">
                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 20 20"
                                    fill="none" aria-hidden="true">
                                    <path
                                        d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                        :fill="(showDataItemInfo as any).has_like
                                                ? '#FF5353'
                                                : 'none'
                                            " :stroke="(showDataItemInfo as any).has_like
                                            ? 'none'
                                            : '#888888'
                                        " stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                                <span class="count">{{
                                    (showDataItemInfo as any).likes_num || 0
                                    }}</span>
                            </button>

                           <button class="icon-btn" :aria-label="t('inspiration.copyLink')" @click.stop="copyLink">
                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22"
                                    fill="none">
                                    <path
                                        d="M16.8301 14.245L15.5284 12.9433L16.8301 11.6416C17.4168 11.055 17.814 10.3705 18.0218 9.5883C18.2295 8.80608 18.2295 8.0208 18.0218 7.23247C17.814 6.44414 17.4168 5.75664 16.8301 5.16997C16.2434 4.5833 15.5559 4.18608 14.7676 3.9783C13.9793 3.77053 13.194 3.77053 12.4118 3.9783C11.6295 4.18608 10.9451 4.5833 10.3584 5.16997L9.05676 6.47164L7.75509 5.16997L9.05676 3.8683C9.87565 3.04942 10.8473 2.4933 11.9718 2.19997C13.0473 1.91886 14.129 1.91886 15.2168 2.19997C16.3412 2.4933 17.3129 3.04942 18.1318 3.8683C18.9506 4.68719 19.5068 5.65886 19.8001 6.7833C20.0812 7.87108 20.0812 8.95275 19.8001 10.0283C19.5068 11.1527 18.9506 12.1244 18.1318 12.9433L16.8301 14.245ZM14.2451 16.83L12.9434 18.1316C12.1245 18.9505 11.1529 19.5066 10.0284 19.8C8.95287 20.0811 7.8712 20.0811 6.78342 19.8C5.65898 19.5066 4.68731 18.9505 3.86842 18.1316C3.04954 17.3127 2.49342 16.3411 2.20009 15.2166C1.91898 14.1289 1.91898 13.0472 2.20009 11.9716C2.49342 10.8472 3.04954 9.87553 3.86842 9.05664L5.17009 7.75497L6.47176 9.05664L5.17009 10.3583C4.58342 10.945 4.1862 11.6294 3.97842 12.4116C3.77065 13.1939 3.77065 13.9791 3.97842 14.7675C4.1862 15.5558 4.58342 16.2433 5.17009 16.83C5.75676 17.4166 6.44426 17.8139 7.23259 18.0216C8.02092 18.2294 8.8062 18.2294 9.58842 18.0216C10.3706 17.8139 11.0551 17.4166 11.6418 16.83L12.9434 15.5283L14.2451 16.83ZM13.5851 7.1133L14.8868 8.41497L8.41509 14.8866L7.11342 13.585L13.5851 7.1133Z"
                                        fill="#888888" />
                                </svg>
                            </button>

                            <button class="icon-btn" :aria-label="t('inspiration.download')"
                                @click.stop="toggleDownloadMenu">
                                <svg width="22" height="22" viewBox="0 0 16 16" fill="none"
                                    xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path
                                        d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
                                        stroke="#888888" stroke-width="1.5" stroke-linecap="round"
                                        stroke-linejoin="round" />
                                    <path d="M4.66699 6.66675L8.00033 10.0001L11.3337 6.66675" stroke="#888888"
                                        stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M8 2V10" stroke="#888888" stroke-width="1.5" stroke-linecap="round"
                                        stroke-linejoin="round" />
                                </svg>
                            </button>
                        </section>
                    </div>
                </div>

                <div class="detail" v-show="isExpanded">
                    <!-- 图片/视频类型：信息展示 -->
                    <div class="origin-imgs">
                        <div
                            class="title"
                            v-if="showDataItemInfo.param?.pic_urls?.length"
                        >
                            {{ t('inspiration.originalImage') }}
                        </div>
                        <div class="imgs-container">
                            <img
                                :src="item"
                                :alt="generateRefImageAlt(index)"
                                v-for="(item, index) in showDataItemInfo
                                    .param?.pic_urls"
                                :key="item"
                            />
                        </div>
                    </div>
                    <div class="row" v-if="(showDataItemInfo as any)?.param?.model">
                        <span class="k">{{ t('inspiration.model') }}</span>
                        <span class="v">{{ (showDataItemInfo as any).param.model }}</span>
                    </div>
                    <div class="row" v-if="(showDataItemInfo as any)?.param?.aspect_ratio">
                        <span class="k">{{ t('inspiration.aspectRatio') }}</span>
                        <span class="v">{{
                            (showDataItemInfo as any).param.aspect_ratio
                            }}</span>
                    </div>
                    <div class="row" v-if="outputSize">
                        <span class="k">{{ t('inspiration.outputSize') }}</span>
                        <span class="v">{{ outputSize }}</span>
                    </div>
                    <div class="row" v-if="(showDataItemInfo as any)?.param?.aspect_ratio">
                        <span class="k">{{ t('inspiration.type') }}</span>
                        <span class="v">{{ showDataItemInfo.type_name }}</span>
                    </div>
                    <div class="row" v-if="outputSize">
                        <span class="k">{{ t('inspiration.createTime') }}</span>
                        <span class="v">{{ formatDate(showDataItemInfo.create_time) }}</span>
                    </div>
                </div>

                <button class="expand-btn" @click="isExpanded = !isExpanded">
                    {{ isExpanded ? t('inspiration.collapseDetails') : t('inspiration.expandDetails') }}
                    <svg v-if="!isExpanded" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path
                            d="M6.00007 6.53L8.47007 4.06L9.18007 4.76L6.00007 7.94L2.82007 4.76L3.53007 4.06L6.00007 6.53Z"
                            fill="#99F768" />
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path
                            d="M6.00007 5.47L3.53007 7.94L2.82007 7.24L6.00007 4.06L9.18007 7.24L8.47007 7.94L6.00007 5.47Z"
                            fill="#99F768" />
                    </svg>
                </button>

                <button class="primary-btn" @click="navigationChange(showDataItemInfo)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="13" viewBox="0 0 14 13" fill="none">
                        <path
                            d="M8.34923 5.34081C8.1109 5.1097 7.97006 4.82804 7.92673 4.49581L7.71006 2.82748L6.2259 3.62915C5.93701 3.78804 5.62645 3.83859 5.29423 3.78081L3.63673 3.46665L3.9509 5.12415C4.00867 5.45637 3.95812 5.76693 3.79923 6.05581L2.99756 7.53998L4.6659 7.75665C4.99812 7.79998 5.27978 7.94081 5.5109 8.17915L6.67006 9.40331L7.38506 7.88665C7.52951 7.58331 7.7534 7.35943 8.05673 7.21498L9.5734 6.49998L8.34923 5.34081ZM8.3709 8.35248L7.1684 10.8658C7.13229 10.9525 7.0709 11.0103 6.98423 11.0391C6.89756 11.068 6.81451 11.0644 6.73506 11.0283C6.69895 11.0066 6.66645 10.9814 6.63756 10.9525L4.72006 8.92665C4.66951 8.86887 4.60451 8.83637 4.52506 8.82915L1.76256 8.47165C1.6759 8.4572 1.60367 8.41387 1.5459 8.34165C1.48812 8.26943 1.46645 8.18998 1.4809 8.10331C1.48812 8.05998 1.49895 8.02387 1.5134 7.99498L2.8459 5.53581C2.88923 5.47081 2.90367 5.4022 2.88923 5.32998L2.36923 2.57831C2.35478 2.49165 2.37284 2.4122 2.4234 2.33998C2.47395 2.26776 2.54256 2.22081 2.62923 2.19915C2.67256 2.19193 2.71228 2.19193 2.7484 2.19915L5.50006 2.71915C5.57228 2.73359 5.6409 2.71915 5.7059 2.67581L8.16506 1.34332C8.23729 1.29998 8.31673 1.29095 8.4034 1.31623C8.49006 1.34151 8.55506 1.39387 8.5984 1.47331C8.62006 1.50943 8.63451 1.54915 8.64173 1.59248L8.99923 4.35498C9.00645 4.43443 9.03895 4.49943 9.09673 4.54998L11.1226 6.46748C11.1876 6.53248 11.2219 6.60831 11.2255 6.69498C11.2291 6.78165 11.1984 6.86109 11.1334 6.93331C11.1045 6.9622 11.072 6.98387 11.0359 6.99831L8.52256 8.20081C8.45034 8.2297 8.39979 8.28026 8.3709 8.35248ZM8.7934 9.39248L9.56256 8.62331L11.8592 10.9308L11.1009 11.6891L8.7934 9.39248Z"
                            fill="black" />
                    </svg>
                    {{ t('inspiration.generateSimilar') }}
                </button>
            </div>

            <!-- 下载 action sheet -->
            <div class="download-sheet" v-if="isDownloadMenuOpen" @click.self="closeDownloadMenu">
                <div class="download-panel" @click.stop>
                    <button class="dl-item" v-if="(showDataItemInfo as any)?.output_with_watermark"
                        @click="downloadWithWatermark">
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
                                stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                stroke-linejoin="round" />
                            <path d="M4.66699 6.66675L8.00033 10.0001L11.3337 6.66675" stroke="currentColor"
                                stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M8 2V10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                stroke-linejoin="round" />
                        </svg>
                        {{ t('inspiration.downloadWithWatermark') }}
                    </button>
                    <button class="dl-item" @click="downloadWithoutWatermark">
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
                                stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                stroke-linejoin="round" />
                            <path d="M4.66699 6.66675L8.00033 10.0001L11.3337 6.66675" stroke="currentColor"
                                stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M8 2V10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                stroke-linejoin="round" />
                        </svg>
                        {{ t('inspiration.downloadWithoutWatermark') }}
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true">
                            <path
                                d="M1.87051 3.46688L4.67003 5.33323L7.46087 1.42605C7.67487 1.12644 8.09127 1.05705 8.39087 1.27105C8.45073 1.31381 8.50307 1.36618 8.54587 1.42605L11.3367 5.33323L14.1362 3.46688C14.4426 3.26265 14.8565 3.34543 15.0607 3.65178C15.1485 3.78353 15.1866 3.94223 15.1681 4.09947L14.0727 13.4111C14.0331 13.7468 13.7486 13.9999 13.4105 13.9999H2.5962C2.25815 13.9999 1.9736 13.7468 1.9341 13.4111L0.838615 4.09947C0.795595 3.73381 1.05715 3.4025 1.42282 3.35948C1.58007 3.34098 1.73877 3.37905 1.87051 3.46688ZM8.0034 9.99991C8.73973 9.99991 9.33673 9.40297 9.33673 8.66657C9.33673 7.93017 8.73973 7.33324 8.0034 7.33324C7.267 7.33324 6.67007 7.93017 6.67007 8.66657C6.67007 9.40297 7.267 9.99991 8.0034 9.99991Z"
                                fill="white" />
                        </svg>
                    </button>
                    <button class="dl-cancel" @click="closeDownloadMenu">
                        {{ t('common.cancel') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="less">
.mobile-ugc-mask {
    position: fixed;
    inset: 0;
    z-index: 999;
    background: #111112;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;

}

.mobile-ugc {
    position: fixed;
    inset: 0;
    display: flex;
    flex-direction: column;
    padding: 12px;
    justify-content: end;
}

.close-btn {
    position: absolute;
    right: 14px;
    top: 14px;
    width: 30px;
    height: 30px;
    border-radius: 999px;
    background: #080809;
    border: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
}

.media-area {
    position: relative;
    flex: 1;
    background: #0d0e12;
    border-radius: 16px;
    overflow: hidden;
    max-height: 50vh;
    max-width: 100vw;
}

.media {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.media-loading {
    position: absolute;
    inset: 0;
    background: #0d0e12;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
}

.is-loading {
    opacity: 0;
}

.sheet {
    margin-top: 12px;
    border-radius: 16px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.sheet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.show-prompt-actions {
    display: flex;
    column-gap: 20px;
    width: 100vw;
}

.userinfo-left {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 12px;
}

.title-block {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.title-avatar {
    width: 36px;
    height: 36px;
    border-radius: 999px;
    flex-shrink: 0;
    overflow: hidden;

    img {
        width: 36px;
        height: 36px;
        object-fit: cover;
    }
}

.title-text {
    color: #FFF;
    font-family: "PingFang SC";
    font-size: 15px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
}

.name {
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 48vw;
}

.sub {
    margin-top: 2px;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.6);
}

.actions {
    display: flex;
    align-items: center;
    /* gap: 8px; */
    flex-shrink: 0;
    flex-direction: column;
    width: 22px;
    justify-content: space-between;
}

.icon-btn {
    height: 30px;
    border-radius: 10px;
    border: none;
    color: #b2b2b2;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    &.like {
        display: flex;
        flex-direction: column;
        height: 45px;
    }
}

.count {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.75);
}

.prompt-card {
    position: relative;
    border-radius: 8px;
    padding: 5px 10px;
    background: #1d1d1d;
    min-width: 270px;
}

.prompt-text {
    color: rgba(255, 255, 255, 0.80);
    font-family: "PingFang SC";
    font-size: 13.8px;
    font-style: normal;
    font-weight: 400;
    line-height: 1.5;
    max-height: 13vh;
    overflow-y: auto;
    /* 152.174% */

    &.multilineText {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2; /* 限制显示的行数 */
        overflow: hidden; /* 隐藏溢出的部分 */
        text-align: justify;
    }
}

.prompt-copy {
    position: absolute;
    right: 8px;
    top: 8px;
    width: 30px;
    height: 30px;
    border-radius: 10px;
    border: none;
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.75);
    display: flex;
    align-items: center;
    justify-content: center;
}

.expand-btn {
    border: none;
    background: transparent;
    color: #99f768;
    font-size: 12px;
    text-align: left;
    padding: 2px 0;
    display: flex;
    align-items: center;
    column-gap: 4px;
}

.detail {
    display: grid;
    gap: 7px;
    padding: 2px 0 6px 0;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    padding-top: 12px;
    margin-right: 50px;
    grid-template-columns: repeat(2, 1fr);
}

.origin-imgs {
    grid-column: span 2;
    margin-bottom: 4px;

    .title {
        font-size: 10px;
        color: #666;
        font-weight: 600;
        margin-bottom: 6px;
    }

    .imgs-container {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;

        img {
            width: 60px;
            height: 60px;
            object-fit: cover;
            border-radius: 6px;
            border: 1px solid rgba(255, 255, 255, 0.1);
        }
    }
}

.row {
    display: flex;
    justify-content: space-between;
    gap: 3px;
    flex-direction: column;
}

.k {
    font-size: 10px;
    color: #666;
    font-weight: 600;
}

.v {
    font-size: 12px;
    color: #ddd;
    font-weight: 400;
}

.primary-btn {
    margin-top: 2px;
    border: none;
    border-radius: 28px;
    background: #99f768;
    color: #000;
    font-size: 15px;
    padding: 16px 14px;
    display: flex;
    justify-content: center;
    align-items: center;
    column-gap: 10px;
    font-weight: 600;
}

.download-sheet {
    position: fixed;
    inset: 0;
    z-index: 1000;
    background: rgba(0, 0, 0, 0.35);
    display: flex;
    align-items: flex-end;
    padding: 12px;
}

.download-panel {
    width: 100%;
    border-radius: 16px;
    overflow: hidden;
    background: #2a2c36;
    border: 1px solid #3a3d4a;
}

.dl-item,
.dl-cancel {
    width: 100%;
    height: 48px;
    border: none;
    background: transparent;
    color: #fff;
    font-size: 14px;
    display: flex;
    justify-content: center;
    align-items: center;
    column-gap: 10px;
}

.dl-item+.dl-item,
.dl-item+.dl-cancel {
    border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.dl-cancel {
    color: rgba(255, 255, 255, 0.7);
}

</style>
